/**
 * #109 · Alta de una marca cliente y de su primer administrador.
 *
 * Un solo formulario para los dos, y no dos pasos: un tenant sin
 * administrador es un cliente al que nadie puede entrar, y arreglarlo exige
 * tocar la base. La API lo crea en una transacción.
 *
 * El identificador se valida mientras se escribe con el mismo patrón que la
 * función de Postgres. La autoridad sigue siendo la API —si igual responde
 * 422 se muestra su mensaje—, pero un error que se ve al tipear ahorra el
 * viaje y explica la regla en el momento en que importa: el slug NO se puede
 * cambiar después, porque es con lo que el cliente entra.
 */
import { useState, type FormEvent, type ReactNode } from 'react';

import { useI18n } from '@/i18n/useI18n';
import { Button } from '@/ui/Button';
import { ErrorState } from '@/ui/states';
import { SelectField, TextField } from '../products/ui/fields';

export interface ClientDraft {
  slug: string;
  legal_name: string;
  country_code: string;
  industry: string;
  consumer_portal_enabled: boolean;
  admin_email: string;
  admin_name: string | null;
}

/** Los cinco de `tenant.industry`. El CHECK de la base es la autoridad. */
const INDUSTRIES = ['general', 'pharma', 'wine_spirits', 'cosmetics', 'food'] as const;

const SLUG_RE = /^[a-z][a-z0-9-]{2,30}$/;

export interface ClientFormProps {
  onSubmit: (draft: ClientDraft) => void;
  onCancel: () => void;
  pending: boolean;
  error: unknown;
}

export function ClientForm({ onSubmit, onCancel, pending, error }: ClientFormProps): ReactNode {
  const { t } = useI18n();
  const [slug, setSlug] = useState('');
  const [legalName, setLegalName] = useState('');
  const [country, setCountry] = useState('PY');
  const [industry, setIndustry] = useState<string>('general');
  const [portal, setPortal] = useState(false);
  const [adminEmail, setAdminEmail] = useState('');
  const [adminName, setAdminName] = useState('');

  // ADR-011: en pharma el portal está prohibido, y el CHECK de la base lo
  // rechaza. Se apaga solo al elegir el rubro para que nadie llene el
  // formulario entero y se entere al enviarlo.
  const esPharma = industry === 'pharma';
  const slugInvalido = slug !== '' && !SLUG_RE.test(slug);
  const listo = SLUG_RE.test(slug) && legalName.trim() !== '' && adminEmail.trim() !== '';

  function enviar(event: FormEvent): void {
    event.preventDefault();
    if (!listo) return;
    onSubmit({
      slug,
      legal_name: legalName.trim(),
      country_code: country.trim().toUpperCase(),
      industry,
      consumer_portal_enabled: esPharma ? false : portal,
      admin_email: adminEmail.trim(),
      admin_name: adminName.trim() === '' ? null : adminName.trim(),
    });
  }

  return (
    <form className="flex flex-col gap-4" onSubmit={enviar}>
      <TextField
        label={t('clients.form.slug')}
        value={slug}
        onChange={(value) => { setSlug(value.toLowerCase()); }}
        hint={t('clients.form.slugHint')}
        error={slugInvalido ? t('clients.form.slugHint') : undefined}
        required
        mono
        maxLength={31}
      />
      <TextField
        label={t('clients.form.legalName')}
        value={legalName}
        onChange={setLegalName}
        required
        maxLength={200}
      />
      <div className="grid gap-3 sm:grid-cols-2">
        <TextField
          label={t('clients.form.country')}
          value={country}
          onChange={(value) => { setCountry(value.toUpperCase()); }}
          maxLength={2}
          mono
        />
        <SelectField
          label={t('clients.form.industry')}
          value={industry}
          onChange={setIndustry}
          options={INDUSTRIES.map((value) => ({ value, label: value }))}
        />
      </div>

      <label className="flex items-start gap-2 text-sm">
        <input
          type="checkbox"
          className="mt-1"
          checked={portal && !esPharma}
          disabled={esPharma}
          onChange={(event) => { setPortal(event.target.checked); }}
        />
        <span>
          {t('clients.form.portal')}
          {esPharma && (
            <span className="block text-xs text-ink-muted">
              {t('clients.form.portalPharma')}
            </span>
          )}
        </span>
      </label>

      <div className="grid gap-3 sm:grid-cols-2">
        <TextField
          label={t('clients.form.adminEmail')}
          value={adminEmail}
          onChange={setAdminEmail}
          required
        />
        <TextField
          label={t('clients.form.adminName')}
          value={adminName}
          onChange={setAdminName}
          maxLength={120}
        />
      </div>

      {error !== null && error !== undefined && <ErrorState error={error} />}

      <div className="flex justify-end gap-2">
        <Button type="button" size="sm" variant="ghost" onClick={onCancel}>
          {t('form.cancel')}
        </Button>
        <Button type="submit" size="sm" variant="primary" disabled={!listo || pending}>
          {t('clients.form.submit')}
        </Button>
      </div>
    </form>
  );
}
